import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Divider,
  Button,
  Stack,
} from "@mui/material";
import React from "react";
import { Helmet } from "react-helmet-async";
import FAQAccordion from "./FAQCollapse";
import EmailIcon from "@mui/icons-material/Email";
import InstagramIcon from "@mui/icons-material/Instagram";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { HashLink } from "react-router-hash-link";
import HomeIcon from "@mui/icons-material/Home";

const About = () => {
  return (
    <>
    <Helmet>
      <title>About Den Den Claw | Ottawa's Claw Machine Arcade</title>
      <meta
        name="description"
        content="Den Den Claw is a Japanese-style claw machine arcade in Ottawa's Chinatown, founded by two local brothers. Learn our story, values, and what to expect when you visit."
      />
      <link rel="canonical" href="https://www.dendenclaw.com/about" />
    </Helmet>
    <Box id="about" sx={{ py: 8 }}>
      <Container maxWidth="md">
        {/* ---------- HEADER ---------- */}
        <Typography
          variant="h4"
          sx={{
            color: "primary.main",
            textAlign: "center",
            fontWeight: 800,
            mb: 1,
          }}
        >
          Den Den Claw
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            mb: 6,
            fontStyle: "italic",
            color: "primary.main",
            opacity: 0.9,
          }}
        >
          Built by fans, for fans
        </Typography>
        <Typography
  sx={{
    textAlign: "center",
    mb: 6,
    color: "text.secondary",
    maxWidth: 720,
    mx: "auto",
  }}
>
  Den Den Claw is a claw machine arcade in Ottawa’s Chinatown, built as a
  home for anime fans, gamers, collectors, and anyone looking for something
  fun to do in the city.
</Typography>


        {/* ---------- OUR STORY ---------- */}
        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Our Story
            </Typography>

            <Typography sx={{ mb: 2 }}>
              Den Den Claw was founded by two local brothers after facing layoffs
              during COVID. Like many others, we found ourselves asking:
              <strong> “What do we really want to build?”</strong>
            </Typography>

            <Typography>
              The answer was simple — something we genuinely love, and a physical
space where people who love anime, video games, claw machines, and
niche hobbies could feel at home.
 And what a better place to make this dream home than in the heart
    of Ottawa's very own <strong>Chinatown?</strong>
            </Typography>
          </CardContent>
        </Card>

        {/* ---------- WHY DEN DEN ---------- */}
        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Our Values
            </Typography>

            <Typography sx={{ mb: 2 }}>
              Inspired by Japanese claw arcades, Den Den Claw isn’t just about luck.
As a skill-based arcade, we focus on helping people <strong>learn how to win.</strong>.
Our staff is
    always happy to give tips, explain machine mechanics, and cheer you on.
    
            </Typography>
          </CardContent>
        </Card>

        {/* ---------- CORE BELIEF ---------- */}
        <Box
          sx={{
            my: 6,
            p: 4,
            borderRadius: 3,
            backgroundColor: "secondary.main",
            textAlign: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Everyone should go home a winner 🏆
          </Typography>

          <Typography>
            That’s why we focus on teaching players how to win — not just
            relying on luck.
          </Typography>
        </Box>

        {/* ---------- WHAT TO EXPECT ---------- */}
        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 6 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              What You’ll Find Here
            </Typography>

            <Typography sx={{ mb: 2 }}>
              Whether it’s a date night, a hangout with friends, or your first
              time playing claw machines, Den Den Claw offers a huge variety of
              prizes — from anime figures and video game merch to cute mascots
              and collectibles.
            </Typography>

            <Typography>
              Snap photos, share your wins, trade prizes, and make memories.
              The fun doesn’t stop at the machine 💙
            </Typography>
          </CardContent>
        </Card>

        {/* ---------- PASTEL DIVIDER ---------- */}
        <Divider
          sx={{
            my: 6,
            height: 3,
            borderRadius: 999,
            background:
              "linear-gradient(90deg, #cfe8ff, #e8f2ff, #cfe8ff)",
          }}
        />

        {/* ---------- FAQ ---------- */}
        <Typography
          variant="h5"
          sx={{
            textAlign: "center",
            fontWeight: 800,
            mb: 1,
          }}
        >
          Got questions?
        </Typography>

        <Typography
          sx={{
            textAlign: "center",
            mb: 4,
            color: "text.secondary",
          }}
        >
          Here’s what people usually ask us
        </Typography>

        <FAQAccordion />

        {/* ---------- STILL HAVE QUESTIONS CTA ---------- */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: 4,
            p: 4,
            mt: 6,
            textAlign: "center",
            background: "linear-gradient(180deg, #e8f2ff 0%, #f5faff 100%)",
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Still have questions? 💭
            </Typography>

            <Typography sx={{ mb: 3, color: "text.secondary" }}>
              We’re always happy to help — reach out anytime!
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                startIcon={<EmailIcon />}
                href={
                  "mailto:dendendori@gmail.com" +
                  "?subject=Visiting%20Den%20Den%20Claw"
                }
                sx={{
                  borderRadius: 999,
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                dendendori@gmail.com
              </Button>

              <Button
                variant="outlined"
                startIcon={<PhoneIphoneIcon />}
                href="tel:16134002909"
                sx={{
                  borderRadius: 999,
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Text (613) 400-2909
              </Button>

              <Button
                variant="outlined"
                startIcon={<InstagramIcon />}
                href="https://instagram.com/dendenclaw"
                target="_blank"
                sx={{
                  borderRadius: 999,
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                DM @dendenclaw
              </Button>
            </Stack>
          </CardContent>
        </Card>
          <Box
            sx={{
              mt: 6,
              display: "flex",
              justifyContent: "center",
            }}
          >

          <HashLink smooth to="/#home" className="text-style">
            <Button
              variant="contained"
              color="primary"
              startIcon={<HomeIcon />}
              sx={{
                px: 4,
                py: 1.2,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Back to Home
            </Button>
          </HashLink>
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default About;
